// CmaPage — the CMA builder for agents (/dashboard/cma, feature #4, 19 Oct 2026).
//   CMAs:            every comparative market analysis in my book, and the website-chat
//                    "home valuation" requests waiting for one (Prepare CMA opens / starts it).
//   Builder (?id=):  subject (from a listing, an office property, the seller's client record, or
//                    typed), comparable search over the listing index (same city or ZIP, type,
//                    beds ±, sq ft ±%, sold within N months, active / pending competition) and
//                    agent-entered comps (labelled), the adjustment values for this CMA, the
//                    adjusted grid, weighted indication, range, $/sq ft and DOM stats, the agent's
//                    pricing recommendation, the market commentary (optional AI draft, a draft
//                    until the agent ticks it as reviewed), save / save version / print report.
//   Office settings: default adjustment values and report branding (MB / Office Manager), the
//                    AI commentary switch (MB).
// Deep links: ?new=1&client_id=N (from the client record), ?new=1&handoff_id=N (valuation request).
// A CMA / broker price opinion is not an appraisal (225 ILCS 454/10-45); the report says so.
// API: /api/cma (routes/cma.js); arithmetic in lib/cma.js.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import { openProtected } from '../../utils/openProtected';

const inp = 'border rounded px-2 py-1 text-sm';
const btn = 'px-3 py-1 rounded text-sm border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50';
const btnDark = 'px-3 py-1 rounded text-sm bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50';
const card = 'bg-white rounded-lg shadow p-4 space-y-3';
const money = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? '—' : `$${Math.round(Number(v)).toLocaleString('en-US')}`);
const signed = (v) => (!Number(v) ? '—' : `${Number(v) > 0 ? '+' : '−'}$${Math.abs(Math.round(Number(v))).toLocaleString('en-US')}`);
const plain = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-US'));
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const ORIGIN = { own: 'Office listing', idx: 'MLS (IDX)', manual: 'Agent-entered' };
const ORIGIN_STYLE = { own: 'bg-blue-100 text-blue-800', idx: 'bg-violet-100 text-violet-800', manual: 'bg-amber-100 text-amber-800' };
const STATUS = { sold: 'Sold', active: 'Active', pending: 'Pending' };
const TYPES = [['residential', 'Residential'], ['condo', 'Condo / townhome'], ['multi_family', 'Multi-family'], ['land', 'Land'], ['commercial', 'Commercial'], ['industrial', 'Industrial']];
// The not-an-appraisal citation follows the site's state (Illinois 225 ILCS 454/10-45, Florida Fla. Stat. 475.611).
const noticeLine = (settings) => `Comparative market analysis / broker price opinion, not an appraisal (${(settings && settings.notice && settings.notice.cite) || '225 ILCS 454/10-45'}).`;

const Pill = ({ origin }) => <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${ORIGIN_STYLE[origin] || 'bg-slate-100'}`}>{ORIGIN[origin] || origin}</span>;

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------
function ListView({ api, onOpen, onCreate }) {
    const [rows, setRows] = useState([]);
    const [handoffs, setHandoffs] = useState([]);
    const [err, setErr] = useState('');
    useEffect(() => {
        api.get('/api/cma').then(setRows).catch((e) => setErr(e.message));
        api.get('/api/cma/handoffs').then(setHandoffs).catch(() => setHandoffs([]));
    }, [api]);
    return (
        <div className="space-y-4">
            {err && <p className="text-sm text-red-700">{err}</p>}
            {handoffs.length > 0 && (
                <div className={card}>
                    <h3 className="font-semibold text-sm">Website home-valuation requests</h3>
                    <p className="text-xs text-slate-500">The website assistant gave the visitor no value: a licensed agent prepares the CMA and contacts them.</p>
                    <ul className="divide-y text-sm">
                        {handoffs.map((h) => (
                            <li key={h.handoff_id} className="py-2 flex flex-wrap gap-2 items-center justify-between">
                                <div>
                                    <div className="font-medium">H-{h.handoff_id} · {h.client_name || 'visitor'} <span className="text-xs text-slate-500">({h.status}, {fmt(h.created_at)})</span></div>
                                    <div className="text-xs text-slate-600 whitespace-pre-line">{h.reason}</div>
                                </div>
                                {h.cma_id
                                    ? <button className={btn} onClick={() => onOpen(h.cma_id)}>Open CMA</button>
                                    : <button className={btnDark} onClick={() => onCreate({ handoff_id: h.handoff_id })}>Prepare CMA</button>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className={card}>
                <div className="flex flex-wrap gap-2 items-center justify-between">
                    <h3 className="font-semibold text-sm">My CMAs</h3>
                    <button className={btnDark} onClick={() => onCreate({})}>New CMA</button>
                </div>
                <div className="mt-3">
                    <DataList rows={rows} rowKey="cma_id" csvName="cmas" defaultSort={{ key: 'updated_at', dir: 'desc' }} empty="No CMAs yet."
                        onRowClick={(r) => onOpen(r.cma_id)} filters={[{ key: 'status', label: 'Status' }, { key: 'agent_name', label: 'Agent' }]}
                        columns={[
                            { key: 'title', label: 'CMA', value: (r) => `${r.title || ''} ${r.address || ''}`.trim(), sortValue: (r) => r.title || '',
                                render: (r) => <><div className="font-medium text-indigo-700">{r.title}</div><div className="text-xs text-slate-500">{r.address}</div></> },
                            { key: 'client_name', label: 'Client', value: (r) => (r.client_id ? r.client_name || `#${r.client_id}` : ''),
                                render: (r) => (r.client_id ? <Link to={`/dashboard/clients/${r.client_id}`} onClick={(e) => e.stopPropagation()} className="underline">{r.client_name || `#${r.client_id}`}</Link> : '—') },
                            { key: 'agent_name', label: 'Agent', render: (r) => r.agent_name || '—' },
                            { key: 'recommended_price', label: 'Recommended', type: 'number', render: (r) => money(r.recommended_price) },
                            { key: 'status', label: 'Status', render: (r) => <>{r.status}{r.current_version ? ` · v${r.current_version}` : ''}{r.handoff_id ? ` · H-${r.handoff_id}` : ''}</> },
                            { key: 'updated_at', label: 'Updated', type: 'date', render: (r) => <span className="whitespace-nowrap">{fmt(r.updated_at)}</span> },
                        ]} />
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Builder pieces
// ---------------------------------------------------------------------------
function NumField({ label, value, onChange, step = 'any', disabled, w = 'w-28' }) {
    return (
        <label className="text-xs text-slate-600 flex flex-col">
            {label}
            <input type="number" step={step} className={`${inp} ${w}`} value={value ?? ''} disabled={disabled}
                onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
        </label>
    );
}
function TextField({ label, value, onChange, disabled, w = 'w-44', type = 'text' }) {
    return (
        <label className="text-xs text-slate-600 flex flex-col">
            {label}
            <input type={type} className={`${inp} ${w}`} value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        </label>
    );
}
function ConditionSelect({ value, onChange, conditions, disabled }) {
    return (
        <select className={inp} value={value || 'average'} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label="Condition">
            {conditions.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
    );
}

function SubjectCard({ api, cma, set, conditions, readOnly }) {
    const s = cma.subject;
    const [src, setSrc] = useState({ listing: '', property: '', client: cma.client_id ? String(cma.client_id) : '' });
    const [err, setErr] = useState('');
    const setS = (k, v) => set({ subject: { ...s, [k]: v } });
    const fill = async (kind) => {
        setErr('');
        try {
            if (kind === 'listing') set({ subject: { ...(await api.get(`/api/cma/subject/listing/${encodeURIComponent(src.listing.trim())}`)), condition: s.condition } });
            if (kind === 'property') set({ subject: { ...(await api.get(`/api/cma/subject/property/${Number(src.property)}`)), condition: s.condition } });
            if (kind === 'client') {
                const r = await api.get(`/api/cma/subject/client/${Number(src.client)}`);
                set({ subject: { ...s, ...Object.fromEntries(Object.entries(r.subject).filter(([, v]) => v !== null && v !== 'typed' && v !== 'residential')), source: 'client' },
                    client_id: Number(src.client), prepared_for: cma.prepared_for || r.prepared_for });
            }
        } catch (e) { setErr(e.message); }
    };
    return (
        <div className={card}>
            <h3 className="font-semibold">Subject property <span className="text-xs font-normal text-slate-500">(source: {s.source})</span></h3>
            {!readOnly && (
                <div className="flex flex-wrap gap-3 items-end text-sm">
                    <span className="text-xs text-slate-500">Fill from:</span>
                    <TextField label="Listing key (e.g. own-12)" value={src.listing} onChange={(v) => setSrc({ ...src, listing: v })} w="w-40" />
                    <button className={btn} disabled={!src.listing.trim()} onClick={() => fill('listing')}>Listing</button>
                    <TextField label="Office property #" value={src.property} onChange={(v) => setSrc({ ...src, property: v })} w="w-24" />
                    <button className={btn} disabled={!Number(src.property)} onClick={() => fill('property')}>Property</button>
                    <TextField label="Seller client #" value={src.client} onChange={(v) => setSrc({ ...src, client: v })} w="w-24" />
                    <button className={btn} disabled={!Number(src.client)} onClick={() => fill('client')}>Client</button>
                </div>
            )}
            {err && <p className="text-sm text-red-700">{err}</p>}
            <div className="flex flex-wrap gap-3">
                <TextField label="Street address" value={s.address} onChange={(v) => setS('address', v)} disabled={readOnly} w="w-64" />
                <TextField label="Unit" value={s.unit} onChange={(v) => setS('unit', v)} disabled={readOnly} w="w-20" />
                <TextField label="City" value={s.city} onChange={(v) => setS('city', v)} disabled={readOnly} w="w-36" />
                <TextField label="State" value={s.state} onChange={(v) => setS('state', v)} disabled={readOnly} w="w-16" />
                <TextField label="ZIP" value={s.zip} onChange={(v) => setS('zip', v)} disabled={readOnly} w="w-24" />
                <label className="text-xs text-slate-600 flex flex-col">Type
                    <select className={inp} value={s.type || 'residential'} disabled={readOnly} onChange={(e) => setS('type', e.target.value)}>
                        {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                </label>
            </div>
            <div className="flex flex-wrap gap-3">
                <NumField label="Living sq ft" value={s.sqft} onChange={(v) => setS('sqft', v)} disabled={readOnly} />
                <NumField label="Bedrooms" value={s.beds} onChange={(v) => setS('beds', v)} disabled={readOnly} w="w-20" />
                <NumField label="Baths (½ = .5)" value={s.baths} onChange={(v) => setS('baths', v)} disabled={readOnly} w="w-20" />
                <NumField label="Garage spaces" value={s.garage} onChange={(v) => setS('garage', v)} disabled={readOnly} w="w-20" />
                <NumField label="Lot (acres)" value={s.lot_acres} onChange={(v) => setS('lot_acres', v)} disabled={readOnly} w="w-24" />
                <NumField label="Year built" value={s.year_built} onChange={(v) => setS('year_built', v)} disabled={readOnly} w="w-24" step="1" />
                <label className="text-xs text-slate-600 flex flex-col">Condition
                    <ConditionSelect value={s.condition} onChange={(v) => setS('condition', v)} conditions={conditions} disabled={readOnly} />
                </label>
            </div>
            <label className="text-xs text-slate-600 flex flex-col">Notes on the subject (printed)
                <textarea className={`${inp} w-full`} rows={2} value={s.notes || ''} disabled={readOnly} onChange={(e) => setS('notes', e.target.value)} />
            </label>
        </div>
    );
}

function CompSearchCard({ api, cma, addComps, defaults }) {
    const [f, setF] = useState({ area: 'city', sold_months: defaults.sold_months, beds_tolerance: defaults.beds_tolerance, sqft_pct: defaults.sqft_pct, sold: true, active: true, pending: true });
    const [res, setRes] = useState(null);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const have = new Set(cma.comps.map((c) => c.listing_key).filter(Boolean));
    const run = async () => {
        setErr(''); setBusy(true);
        try { setRes(await api.post('/api/cma/comps/search', { subject: cma.subject, filters: f })); } catch (e) { setErr(e.message); }
        setBusy(false);
    };
    return (
        <div className={card}>
            <h3 className="font-semibold">Find comparables <span className="text-xs font-normal text-slate-500">(listing index: office listings, and MLS listings when the feed is current)</span></h3>
            <div className="flex flex-wrap gap-3 items-end text-sm">
                <label className="text-xs text-slate-600 flex flex-col">Area
                    <select className={inp} value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })}>
                        <option value="city">Same city</option><option value="zip">Same ZIP</option>
                    </select>
                </label>
                <NumField label="Sold within (months)" value={f.sold_months} onChange={(v) => setF({ ...f, sold_months: v })} w="w-20" step="1" />
                <NumField label="Beds ±" value={f.beds_tolerance} onChange={(v) => setF({ ...f, beds_tolerance: v })} w="w-16" step="1" />
                <NumField label="Sq ft ±%" value={f.sqft_pct} onChange={(v) => setF({ ...f, sqft_pct: v })} w="w-16" step="1" />
                {[['sold', 'Sold'], ['active', 'Active'], ['pending', 'Pending']].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} />{l}</label>
                ))}
                <button className={btnDark} disabled={busy} onClick={run}>{busy ? 'Searching…' : 'Search'}</button>
            </div>
            {err && <p className="text-sm text-red-700">{err}</p>}
            {res && (
                <div className="space-y-2">
                    {res.note && <p className="text-sm text-amber-700">{res.note}</p>}
                    {res.applied && res.applied.length > 0 && <p className="text-xs text-slate-500">Filters: {res.applied.join(' · ')}</p>}
                    <div className="flex gap-2">
                        <button className={btn} disabled={!res.comps.some((c) => !have.has(c.listing_key))} onClick={() => addComps(res.comps.filter((c) => !have.has(c.listing_key)))}>Add all ({res.comps.filter((c) => !have.has(c.listing_key)).length})</button>
                    </div>
                    <div className="overflow-x-auto max-h-80 overflow-y-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-slate-50 text-left sticky top-0"><tr>
                                <th className="p-1">Address</th><th className="p-1">Status</th><th className="p-1">Price</th><th className="p-1">Date</th><th className="p-1">Sq ft</th><th className="p-1">Bd/Ba</th><th className="p-1">Built</th><th className="p-1">DOM</th><th className="p-1">Source</th><th className="p-1" />
                            </tr></thead>
                            <tbody>
                                {res.comps.map((c) => (
                                    <tr key={c.id} className="border-t">
                                        <td className="p-1">{c.address}, {c.city}</td><td className="p-1">{STATUS[c.status]}</td><td className="p-1">{money(c.price)}</td>
                                        <td className="p-1">{c.sale_date || c.listed_at || '—'}</td><td className="p-1">{plain(c.sqft)}</td><td className="p-1">{plain(c.beds)}/{plain(c.baths)}</td>
                                        <td className="p-1">{plain(c.year_built)}</td><td className="p-1">{plain(c.dom)}</td><td className="p-1"><Pill origin={c.origin} />{c.attribution && <div className="text-slate-400">{c.attribution}</div>}</td>
                                        <td className="p-1">{have.has(c.listing_key) ? <span className="text-slate-400">added</span> : <button className="underline" onClick={() => addComps([c])}>Add</button>}</td>
                                    </tr>
                                ))}
                                {!res.comps.length && <tr><td colSpan={10} className="p-2 text-slate-500">Nothing matched. Widen the filters or add comparables by hand.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    {res.disclaimer && <p className="text-xs text-slate-500 whitespace-pre-line">{res.disclaimer}</p>}
                </div>
            )}
        </div>
    );
}

const BLANK_MANUAL = { status: 'sold', address: '', city: '', zip: '', price: null, sale_date: '', listed_at: '', dom: null, sqft: null, beds: null, baths: null, garage: null, lot_acres: null, year_built: null, condition: 'average', mls_number: '', notes: '' };
function ManualCompCard({ api, addComps, conditions }) {
    const [c, setC] = useState(BLANK_MANUAL);
    const [err, setErr] = useState('');
    const [open, setOpen] = useState(false);
    const set = (k, v) => setC({ ...c, [k]: v });
    const add = async () => {
        setErr('');
        try { addComps([await api.post('/api/cma/comps/manual', { comp: c })]); setC(BLANK_MANUAL); } catch (e) { setErr(e.message); }
    };
    return (
        <div className={card}>
            <div className="flex justify-between items-center">
                <h3 className="font-semibold">Add a comparable by hand <span className="text-xs font-normal text-slate-500">(labelled "Agent-entered" on the report)</span></h3>
                <button className={btn} onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'}</button>
            </div>
            {open && (
                <>
                    <div className="flex flex-wrap gap-3">
                        <label className="text-xs text-slate-600 flex flex-col">Status
                            <select className={inp} value={c.status} onChange={(e) => set('status', e.target.value)}>
                                <option value="sold">Sold</option><option value="active">Active</option><option value="pending">Pending</option>
                            </select>
                        </label>
                        <TextField label="Street address" value={c.address} onChange={(v) => set('address', v)} w="w-56" />
                        <TextField label="City" value={c.city} onChange={(v) => set('city', v)} w="w-32" />
                        <TextField label="ZIP" value={c.zip} onChange={(v) => set('zip', v)} w="w-20" />
                        <NumField label={c.status === 'sold' ? 'Sold price' : 'List price'} value={c.price} onChange={(v) => set('price', v)} />
                        {c.status === 'sold' && <TextField type="date" label="Sold date" value={c.sale_date} onChange={(v) => set('sale_date', v)} w="w-36" />}
                        <TextField type="date" label="Listed" value={c.listed_at} onChange={(v) => set('listed_at', v)} w="w-36" />
                        <NumField label="DOM" value={c.dom} onChange={(v) => set('dom', v)} w="w-16" step="1" />
                        <TextField label="MLS # (if any)" value={c.mls_number} onChange={(v) => set('mls_number', v)} w="w-28" />
                    </div>
                    <div className="flex flex-wrap gap-3 items-end">
                        <NumField label="Sq ft" value={c.sqft} onChange={(v) => set('sqft', v)} w="w-24" />
                        <NumField label="Beds" value={c.beds} onChange={(v) => set('beds', v)} w="w-16" />
                        <NumField label="Baths" value={c.baths} onChange={(v) => set('baths', v)} w="w-16" />
                        <NumField label="Garage" value={c.garage} onChange={(v) => set('garage', v)} w="w-16" />
                        <NumField label="Lot ac" value={c.lot_acres} onChange={(v) => set('lot_acres', v)} w="w-20" />
                        <NumField label="Built" value={c.year_built} onChange={(v) => set('year_built', v)} w="w-20" step="1" />
                        <label className="text-xs text-slate-600 flex flex-col">Condition<ConditionSelect value={c.condition} onChange={(v) => set('condition', v)} conditions={conditions} /></label>
                        <TextField label="Notes" value={c.notes} onChange={(v) => set('notes', v)} w="w-56" />
                        <button className={btnDark} onClick={add}>Add comparable</button>
                    </div>
                    {err && <p className="text-sm text-red-700">{err}</p>}
                </>
            )}
        </div>
    );
}

function CompsCard({ cma, set, analysis, conditions, readOnly }) {
    const byId = Object.fromEntries(((analysis && analysis.comps) || []).map((c) => [c.id, c]));
    const setComp = (id, patch) => set({ comps: cma.comps.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
    const remove = (id) => set({ comps: cma.comps.filter((c) => c.id !== id) });
    const custom = cma.weighting === 'custom';
    return (
        <div className={card}>
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h3 className="font-semibold">Comparables ({cma.comps.length})</h3>
                <label className="text-sm flex items-center gap-2">Weighting
                    <select className={inp} value={cma.weighting} disabled={readOnly} onChange={(e) => set({ weighting: e.target.value })}>
                        <option value="equal">Equal</option><option value="custom">Custom weights</option>
                    </select>
                </label>
            </div>
            <p className="text-xs text-slate-500">Fill in what the listing left out (garage, condition). Only comps ticked "use in value" count toward the indication; active and pending listings show as competition.</p>
            <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead className="bg-slate-50 text-left"><tr>
                        <th className="p-1">Comparable</th><th className="p-1">Status / price</th><th className="p-1">Sq ft · Bd/Ba · Built</th><th className="p-1">Garage</th><th className="p-1">Condition</th>
                        <th className="p-1">Use in value</th>{custom && <th className="p-1">Weight</th>}<th className="p-1">Adjusted</th><th className="p-1" />
                    </tr></thead>
                    <tbody>
                        {cma.comps.map((c) => {
                            const a = byId[c.id] && byId[c.id].adjustment;
                            return (
                                <tr key={c.id} className="border-t align-top">
                                    <td className="p-1"><div className="font-medium">{c.address}</div><div className="text-slate-500">{c.city} {c.zip}</div><Pill origin={c.origin} />{c.mls_number && <span className="ml-1 text-slate-400">MLS# {c.mls_number}</span>}</td>
                                    <td className="p-1">{STATUS[c.status]} {money(c.price)}<div className="text-slate-500">{c.sale_date || c.listed_at || ''}{c.dom !== null && c.dom !== undefined ? ` · ${c.dom} DOM` : ''}</div></td>
                                    <td className="p-1">{plain(c.sqft)} · {plain(c.beds)}/{plain(c.baths)} · {plain(c.year_built)}</td>
                                    <td className="p-1"><input type="number" className={`${inp} w-14`} value={c.garage ?? ''} disabled={readOnly} aria-label="Garage spaces"
                                        onChange={(e) => setComp(c.id, { garage: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                                    <td className="p-1"><ConditionSelect value={c.condition} onChange={(v) => setComp(c.id, { condition: v })} conditions={conditions} disabled={readOnly} /></td>
                                    <td className="p-1 text-center"><input type="checkbox" checked={!!c.use_in_value} disabled={readOnly} aria-label="Use in value" onChange={(e) => setComp(c.id, { use_in_value: e.target.checked })} /></td>
                                    {custom && <td className="p-1"><input type="number" min="0" step="0.5" className={`${inp} w-14`} value={c.weight ?? 1} disabled={readOnly} aria-label="Weight"
                                        onChange={(e) => setComp(c.id, { weight: e.target.value === '' ? 0 : Number(e.target.value) })} /></td>}
                                    <td className="p-1 whitespace-nowrap">{a ? <>{money(a.adjusted_price)}<div className="text-slate-500">net {signed(a.net)}{a.net_pct !== null ? ` (${a.net_pct}%)` : ''}</div>
                                        {analysis.value.weights[c.id] !== undefined && <div className="text-slate-500">weight {analysis.value.weights[c.id]}%</div>}
                                        {a.warnings.map((w) => <div key={w} className="text-amber-700 max-w-xs whitespace-normal">{w}</div>)}</> : '—'}</td>
                                    <td className="p-1">{!readOnly && <button className="underline text-red-700" onClick={() => remove(c.id)}>Remove</button>}</td>
                                </tr>
                            );
                        })}
                        {!cma.comps.length && <tr><td colSpan={9} className="p-2 text-slate-500">No comparables yet: search above or add one by hand.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AdjustmentsCard({ cma, set, labels, readOnly, officeDefaults }) {
    const keys = ['sqft_value', 'bedroom_value', 'bathroom_value', 'garage_value', 'lot_acre_value', 'age_year_value', 'condition_value', 'trend_pct_month', 'range_pct', 'rounding', 'net_warn_pct', 'gross_warn_pct'];
    return (
        <div className={card}>
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h3 className="font-semibold">Adjustment values for this CMA</h3>
                {!readOnly && <button className={btn} onClick={() => set({ adjustments: { ...officeDefaults } })}>Reset to office defaults</button>}
            </div>
            <p className="text-xs text-slate-500">Adjustments are made to the comparable: where the subject is better, the comp is adjusted up; where the comp is better, down. Only sold comps move with the market trend.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {keys.map((k) => (
                    <label key={k} className="text-xs text-slate-600 flex justify-between items-center gap-2 border rounded px-2 py-1">
                        <span>{labels[k] || k}</span>
                        <input type="number" step="any" className={`${inp} w-24`} value={cma.adjustments[k] ?? ''} disabled={readOnly}
                            onChange={(e) => set({ adjustments: { ...cma.adjustments, [k]: e.target.value === '' ? null : Number(e.target.value) } })} />
                    </label>
                ))}
            </div>
        </div>
    );
}

function AnalysisCard({ analysis }) {
    if (!analysis) return null;
    const v = analysis.value;
    const st = analysis.stats;
    const valued = analysis.comps.filter((c) => c.use_in_value);
    const row = (label, block) => (
        <tr className="border-t"><td className="p-1">{label}</td><td className="p-1">{block.count}</td><td className="p-1">{money(block.price.median)}</td>
            <td className="p-1">{money(block.price.min)}–{money(block.price.max)}</td><td className="p-1">{money(block.price_per_sqft.median)}</td><td className="p-1">{plain(block.dom.median)}</td></tr>
    );
    return (
        <div className={card}>
            <h3 className="font-semibold">Analysis</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="border rounded p-2"><div className="text-xs text-slate-500">Weighted indication ({v.method})</div><div className="text-lg font-semibold">{money(v.indication_rounded)}</div></div>
                <div className="border rounded p-2"><div className="text-xs text-slate-500">Suggested range</div><div className="font-semibold">{money(v.suggested_low)} – {money(v.suggested_high)}</div></div>
                <div className="border rounded p-2"><div className="text-xs text-slate-500">Adjusted comps low / high</div><div className="font-semibold">{money(v.adjusted_low)} – {money(v.adjusted_high)}</div></div>
                <div className="border rounded p-2"><div className="text-xs text-slate-500">Indicated $/sq ft</div><div className="font-semibold">{money(v.indicated_price_per_sqft)}</div></div>
            </div>
            {analysis.warnings.map((w) => <p key={w} className="text-sm text-amber-700">{w}</p>)}
            <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead className="bg-slate-50 text-left"><tr><th className="p-1" /><th className="p-1">Count</th><th className="p-1">Median price</th><th className="p-1">Price range</th><th className="p-1">Median $/sq ft</th><th className="p-1">Median DOM</th></tr></thead>
                    <tbody>{row('Sold', st.sold)}{row('Active', st.active)}{row('Pending', st.pending)}</tbody>
                </table>
            </div>
            {valued.length > 0 && (
                <div className="overflow-x-auto">
                    <h4 className="font-semibold text-sm mt-2">Adjustment grid</h4>
                    <table className="min-w-full text-xs">
                        <thead className="bg-slate-50 text-left"><tr><th className="p-1">Feature</th>{valued.map((c) => <th key={c.id} className="p-1">{c.address}</th>)}</tr></thead>
                        <tbody>
                            <tr className="border-t"><td className="p-1">Price</td>{valued.map((c) => <td key={c.id} className="p-1">{money(c.price)}</td>)}</tr>
                            {valued[0].adjustment.lines.map((l, i) => (
                                <tr key={l.key} className="border-t"><td className="p-1">{l.label}</td>
                                    {valued.map((c) => { const x = c.adjustment.lines[i]; return <td key={c.id} className="p-1">{x ? <>{x.comp ?? '—'} <span className={x.amount ? 'font-medium' : 'text-slate-400'}>{x.missing ? 'n/a' : signed(x.amount)}</span></> : '—'}</td>; })}
                                </tr>
                            ))}
                            <tr className="border-t font-semibold"><td className="p-1">Adjusted price</td>{valued.map((c) => <td key={c.id} className="p-1">{money(c.adjustment.adjusted_price)}</td>)}</tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function NarrativeCard({ api, cma, set, ai, persist, readOnly }) {
    const [msg, setMsg] = useState('');
    const [findings, setFindings] = useState([]);
    const [busy, setBusy] = useState(false);
    const draft = async () => {
        setMsg(''); setBusy(true);
        try {
            await persist();
            const r = await api.post(`/api/cma/${cma.cma_id}/narrative`, {});
            set({ narrative: r.narrative, narrative_source: 'ai', narrative_reviewed: false }, true);
            setFindings(r.findings || []); setMsg(r.note);
        } catch (e) { setMsg(e.message); }
        setBusy(false);
    };
    const review = async () => {
        setMsg('');
        try {
            await persist();
            const r = await api.post(`/api/cma/${cma.cma_id}/narrative/review`, {});
            set({ narrative_reviewed: true }, true); setFindings(r.findings || []); setMsg('Marked as reviewed: it will print on the report.');
        } catch (e) { setMsg(e.message); setFindings((e.payload && e.payload.findings) || []); }
    };
    return (
        <div className={card}>
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h3 className="font-semibold">Market commentary <span className="text-xs font-normal text-slate-500">({cma.narrative_source === 'ai' ? 'AI draft' : 'agent'}{cma.narrative ? (cma.narrative_reviewed ? ', reviewed' : ', not yet reviewed: not printed') : ''})</span></h3>
                {!readOnly && ai && ai.available && <button className={btn} disabled={busy} onClick={draft}>{busy ? 'Drafting…' : 'Draft with AI'}</button>}
            </div>
            {!readOnly && ai && !ai.available && <p className="text-xs text-slate-500">AI drafting is off{ai.enabled ? ' (no Anthropic API key under Integrations)' : ' (the managing broker switches it on in Office settings)'}. Write the commentary yourself.</p>}
            <textarea className={`${inp} w-full`} rows={6} value={cma.narrative || ''} disabled={readOnly}
                onChange={(e) => set({ narrative: e.target.value, narrative_reviewed: false })} placeholder="What the comparable sales and the active competition suggest about pricing. Facts only: no descriptions of the neighbourhood or who lives there." />
            {findings.length > 0 && (
                <ul className="text-xs text-red-700 list-disc ml-5">{findings.map((f, i) => <li key={i}>"{f.term}" — {f.why}{f.suggestion ? ` ${f.suggestion}` : ''}</li>)}</ul>
            )}
            {!readOnly && cma.narrative && !cma.narrative_reviewed && (
                <button className={btnDark} onClick={review}>I have reviewed this commentary</button>
            )}
            {msg && <p className="text-sm text-slate-700">{msg}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------
function Builder({ api, id, settings, onBack, readOnly }) {
    const [cma, setCma] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [reportVersion, setReportVersion] = useState('');
    const timer = useRef(null);

    useEffect(() => {
        api.get(`/api/cma/${id}`).then((r) => { setCma(r); setAnalysis(r.analysis); setDirty(false); }).catch((e) => setErr(e.message));
    }, [api, id]);

    // Re-run the analysis (server, stateless) shortly after an edit.
    useEffect(() => {
        if (!cma || !dirty) return undefined;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            api.post('/api/cma/analyze', { subject: cma.subject, comps: cma.comps, adjustments: cma.adjustments, weighting: cma.weighting, as_of: cma.as_of })
                .then(setAnalysis).catch(() => {});
        }, 400);
        return () => clearTimeout(timer.current);
    }, [api, cma, dirty]);

    const set = useCallback((patch, clean = false) => { setCma((c) => ({ ...c, ...patch })); if (!clean) setDirty(true); }, []);
    const addComps = (list) => set({ comps: [...cma.comps, ...list.filter((n) => !cma.comps.some((c) => c.id === n.id))] });

    const persist = useCallback(async () => {
        if (!cma) return null;
        const body = { title: cma.title, status: cma.status, subject: cma.subject, comps: cma.comps, adjustments: cma.adjustments, weighting: cma.weighting,
            as_of: cma.as_of, purpose: cma.purpose, prepared_for: cma.prepared_for, client_id: cma.client_id || null,
            recommended_price: cma.recommended_price, recommended_low: cma.recommended_low, recommended_high: cma.recommended_high,
            recommendation_text: cma.recommendation_text, narrative: cma.narrative };
        const r = await api.put(`/api/cma/${cma.cma_id}`, body);
        setCma((c) => ({ ...r, versions: c.versions })); setAnalysis(r.analysis); setDirty(false);
        return r;
    }, [api, cma]);

    const save = async () => { setErr(''); try { await persist(); setMsg('Saved.'); } catch (e) { setErr(e.message); } };
    const version = async () => {
        setErr('');
        const note = window.prompt('A note for this version (e.g. "sent to seller"):', '') ?? null;
        if (note === null) return;
        try {
            await persist();
            const r = await api.post(`/api/cma/${cma.cma_id}/versions`, { note });
            const full = await api.get(`/api/cma/${cma.cma_id}`);
            setCma(full); setAnalysis(full.analysis); setMsg(`Version ${r.version_no} saved.`);
        } catch (e) { setErr(e.message); }
    };
    const print = async () => {
        if (dirty && !readOnly) { try { await persist(); } catch (e) { setErr(e.message); return; } }
        openProtected(`/api/cma/${cma.cma_id}/report${reportVersion ? `?version=${reportVersion}` : ''}`);
    };
    const remove = async () => {
        if (!window.confirm('Delete this CMA? Its saved versions go with it from the list.')) return;
        try { await api.delete(`/api/cma/${cma.cma_id}`); onBack(); } catch (e) { setErr(e.message); }
    };

    if (err && !cma) return <p className="text-sm text-red-700">{err}</p>;
    if (!cma) return <p className="text-sm text-slate-500">Loading…</p>;
    return (
        <div className="space-y-4">
            <div className={card}>
                <div className="flex flex-wrap gap-2 items-center justify-between">
                    <button className="text-sm underline" onClick={onBack}>← All CMAs</button>
                    <div className="flex flex-wrap gap-2 items-center">
                        {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
                        {!readOnly && <button className={btnDark} onClick={save}>Save</button>}
                        {!readOnly && <button className={btn} onClick={version}>Save version</button>}
                        <select className={inp} value={reportVersion} onChange={(e) => setReportVersion(e.target.value)} aria-label="Report version">
                            <option value="">Working copy</option>
                            {(cma.versions || []).map((v) => <option key={v.version_no} value={v.version_no}>v{v.version_no}{v.note ? ` — ${v.note}` : ''} ({fmt(v.created_at)})</option>)}
                        </select>
                        <button className={btn} onClick={print}>Print report</button>
                        {!readOnly && <button className={`${btn} text-red-700`} onClick={remove}>Delete</button>}
                    </div>
                </div>
                {err && <p className="text-sm text-red-700">{err}</p>}
                {msg && <p className="text-sm text-emerald-700">{msg}</p>}
                <div className="flex flex-wrap gap-3">
                    <TextField label="Title" value={cma.title} onChange={(v) => set({ title: v })} disabled={readOnly} w="w-72" />
                    <TextField label="Prepared for" value={cma.prepared_for} onChange={(v) => set({ prepared_for: v })} disabled={readOnly} />
                    <TextField type="date" label="Effective date" value={cma.as_of} onChange={(v) => set({ as_of: v })} disabled={readOnly} w="w-36" />
                    <label className="text-xs text-slate-600 flex flex-col">Status
                        <select className={inp} value={cma.status} disabled={readOnly} onChange={(e) => set({ status: e.target.value })}>
                            <option value="draft">Draft</option><option value="final">Final</option>
                        </select>
                    </label>
                </div>
                <TextField label="Intended purpose (printed; required for a broker price opinion)" value={cma.purpose} onChange={(v) => set({ purpose: v })} disabled={readOnly} w="w-full" />
                <div className="text-xs text-slate-500 flex flex-wrap gap-3">
                    {cma.client_id ? <Link className="underline" to={`/dashboard/clients/${cma.client_id}`}>Seller's client record #{cma.client_id}</Link> : <span>Not linked to a client yet (Fill from: Client below).</span>}
                    {cma.handoff_id && <span>Answers website valuation request H-{cma.handoff_id}</span>}
                    <span>{noticeLine(settings)}</span>
                </div>
            </div>
            <SubjectCard api={api} cma={cma} set={set} conditions={settings.conditions} readOnly={readOnly} />
            {!readOnly && <CompSearchCard api={api} cma={cma} addComps={addComps} defaults={settings.adjustments} />}
            {!readOnly && <ManualCompCard api={api} addComps={addComps} conditions={settings.conditions} />}
            <CompsCard cma={cma} set={set} analysis={analysis} conditions={settings.conditions} readOnly={readOnly} />
            <AdjustmentsCard cma={cma} set={set} labels={settings.labels} readOnly={readOnly} officeDefaults={settings.adjustments} />
            <AnalysisCard analysis={analysis} />
            <div className={card}>
                <h3 className="font-semibold">My pricing recommendation</h3>
                <p className="text-xs text-slate-500">Your opinion, printed as the agent's recommendation. The indication above is arithmetic on the comps you chose; you decide the price.</p>
                <div className="flex flex-wrap gap-3 items-end">
                    <NumField label="Recommended list price" value={cma.recommended_price} onChange={(v) => set({ recommended_price: v })} disabled={readOnly} w="w-36" />
                    <NumField label="Range low" value={cma.recommended_low} onChange={(v) => set({ recommended_low: v })} disabled={readOnly} w="w-32" />
                    <NumField label="Range high" value={cma.recommended_high} onChange={(v) => set({ recommended_high: v })} disabled={readOnly} w="w-32" />
                    {!readOnly && analysis && analysis.value.indication_rounded !== null && (
                        <button className={btn} onClick={() => set({ recommended_price: analysis.value.indication_rounded, recommended_low: analysis.value.suggested_low, recommended_high: analysis.value.suggested_high })}>Use the indication</button>
                    )}
                </div>
                <textarea className={`${inp} w-full`} rows={3} value={cma.recommendation_text || ''} disabled={readOnly} onChange={(e) => set({ recommendation_text: e.target.value })}
                    placeholder="Why this price: the strongest comparables, the competition, the condition, the strategy." />
            </div>
            <NarrativeCard api={api} cma={cma} set={set} ai={settings.ai} persist={persist} readOnly={readOnly} />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Office settings
// ---------------------------------------------------------------------------
function SettingsView({ api, settings, reload }) {
    const [adj, setAdj] = useState(settings.adjustments);
    const [brand, setBrand] = useState(settings.branding);
    const [msg, setMsg] = useState('');
    const canEdit = settings.can_edit;
    const save = async () => {
        setMsg('');
        try { await api.put('/api/cma/settings', { adjustments: adj, branding: brand }); setMsg('Saved.'); reload(); } catch (e) { setMsg(e.message); }
    };
    const switchAi = async (on) => {
        setMsg('');
        try { await api.put('/api/cma/settings', { ai_enabled: on }); reload(); } catch (e) { setMsg(e.message); }
    };
    return (
        <div className="space-y-4">
            <div className={card}>
                <h3 className="font-semibold">Office default adjustment values</h3>
                <p className="text-xs text-slate-500">Every new CMA starts with these; the agent can change them per CMA. {canEdit ? '' : 'Only the managing broker or office manager edits these.'}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {Object.keys(settings.defaults).map((k) => (
                        <label key={k} className="text-xs text-slate-600 flex justify-between items-center gap-2 border rounded px-2 py-1">
                            <span>{settings.labels[k] || k}</span>
                            <input type="number" step="any" className={`${inp} w-24`} value={adj[k] ?? ''} disabled={!canEdit}
                                onChange={(e) => setAdj({ ...adj, [k]: e.target.value === '' ? null : Number(e.target.value) })} />
                        </label>
                    ))}
                </div>
                {canEdit && <button className={btn} onClick={() => setAdj({ ...settings.defaults })}>Restore the built-in starting values</button>}
            </div>
            <div className={card}>
                <h3 className="font-semibold">Report branding</h3>
                <div className="flex flex-wrap gap-3">
                    <TextField label="Report title" value={brand.report_title} onChange={(v) => setBrand({ ...brand, report_title: v })} disabled={!canEdit} w="w-64" />
                    <TextField label="Accent colour (#hex)" value={brand.accent_color} onChange={(v) => setBrand({ ...brand, accent_color: v })} disabled={!canEdit} w="w-28" />
                    <TextField label="Logo URL (https:// or /path)" value={brand.logo_url} onChange={(v) => setBrand({ ...brand, logo_url: v })} disabled={!canEdit} w="w-72" />
                    <TextField label="Tagline" value={brand.tagline} onChange={(v) => setBrand({ ...brand, tagline: v })} disabled={!canEdit} w="w-72" />
                </div>
                <label className="text-xs text-slate-600 flex flex-col">Footer note
                    <textarea className={`${inp} w-full`} rows={2} value={brand.footer_note || ''} disabled={!canEdit} onChange={(e) => setBrand({ ...brand, footer_note: e.target.value })} />
                </label>
                <p className="text-xs text-slate-500">The not-an-appraisal notice ({settings.notice && settings.notice.cite}) and, when MLS data is used, the MLS disclaimer always print; they cannot be removed here.</p>
                {canEdit && <button className={btnDark} onClick={save}>Save settings</button>}
            </div>
            <div className={card}>
                <h3 className="font-semibold">AI market commentary</h3>
                <p className="text-sm">Status: <b>{settings.ai.enabled ? 'on' : 'off'}</b>{settings.ai.api_key_configured ? '' : ' · no Anthropic API key set under Integrations'}</p>
                <p className="text-xs text-slate-500">When on, agents may ask for a draft commentary from the figures only (no client names). Every draft is logged, checked for fair-housing wording, and is not printed until the agent ticks it as reviewed.</p>
                {settings.can_switch_ai && <button className={btn} onClick={() => switchAi(!settings.ai.enabled)}>{settings.ai.enabled ? 'Switch off' : 'Switch on'}</button>}
            </div>
            {msg && <p className="text-sm text-slate-700">{msg}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
export default function CmaPage() {
    const api = useApiClient();
    const user = getCurrentUser();
    // Examiners never reach the API; a Read Only staff login may look but every change is refused server-side.
    const readOnly = user?.role === 'examiner' || (user?.staffRole === 'Read Only' && !user?.isManagingBroker);
    const [params, setParams] = useSearchParams();
    const [settings, setSettings] = useState(null);
    const [err, setErr] = useState('');
    const creating = useRef(false);
    const id = params.get('id');
    const tab = params.get('tab') || 'cmas';

    const loadSettings = useCallback(() => api.get('/api/cma/settings').then(setSettings).catch((e) => setErr(e.message)), [api]);
    useEffect(() => { loadSettings(); }, [loadSettings]);

    const create = useCallback(async (body) => {
        if (creating.current) return;
        creating.current = true;
        setErr('');
        try {
            const r = await api.post('/api/cma', body);
            setParams({ id: String(r.cma_id) });
        } catch (e) { setErr(e.message); }
        creating.current = false;
    }, [api, setParams]);

    // Deep links from the client record, the property page and the valuation requests.
    useEffect(() => {
        if (params.get('new') !== '1' || readOnly) return;
        const body = {};
        if (params.get('client_id')) body.client_id = Number(params.get('client_id'));
        if (params.get('handoff_id')) body.handoff_id = Number(params.get('handoff_id'));
        if (params.get('property_id')) body.property_id = Number(params.get('property_id'));
        create(body);
    }, [params, create, readOnly]);

    return (
        <div className="p-6 space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">CMA Builder</h1>
                <p className="text-sm text-slate-500">{noticeLine(settings)} The agent picks the comparables, the adjustments and the price; the report prints the agent's name and licence number.</p>
            </div>
            <div className="flex flex-wrap gap-2 border-b">
                {[['cmas', 'CMAs'], ['settings', 'Office settings']].map(([k, l]) => (
                    <button key={k} onClick={() => setParams(k === 'cmas' ? {} : { tab: k })}
                        className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k && !(k === 'settings' && id) ? 'border-slate-800 font-semibold' : 'border-transparent text-slate-500'}`}>{l}</button>
                ))}
            </div>
            {err && <p className="text-sm text-red-700">{err}</p>}
            {settings && id && <Builder key={id} api={api} id={id} settings={settings} readOnly={readOnly} onBack={() => setParams({})} />}
            {settings && !id && tab === 'cmas' && (params.get('new') !== '1' || !!err) && <ListView api={api} onOpen={(cid) => setParams({ id: String(cid) })} onCreate={create} />}
            {settings && !id && tab === 'settings' && <SettingsView key={JSON.stringify(settings)} api={api} settings={settings} reload={loadSettings} />}
        </div>
    );
}
